'use client'

import { useCallback, useEffect, useState } from 'react'

/**
 * Global Cmd/Ctrl+K handler for the "jump to column" command palette.
 *
 * Guards:
 *  - Ignores the shortcut when the user is typing in an input, textarea, or a
 *    contenteditable region (e.g. the filter bar, placement-id input). Matches
 *    the usual expectation that Cmd+K doesn't fire while filling a form.
 *  - Uses `metaKey || ctrlKey` so the same binding works on both macOS and Windows
 *    without duplicate handlers.
 *  - `preventDefault()` stops the browser's default Ctrl+K (address-bar focus in
 *    Firefox, no-op in Chrome) and Safari's Cmd+K.
 */
export function useJumpToColumn() {
  const [isOpen, setIsOpen] = useState(false)

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])

  useEffect(() => {
    const isEditableTarget = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false
      const tag = target.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
      if (target.isContentEditable) return true
      return false
    }

    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return
      if (e.key.toLowerCase() !== 'k') return
      if (isEditableTarget(e.target)) return
      e.preventDefault()
      setIsOpen(prev => !prev)
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return { isOpen, open, close, setIsOpen }
}
